import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import type { MeResponse } from "@lms/shared-types";
import { clientIdForHost, type RuntimeConfig } from "../config";

export interface Session {
  idToken: string;
  /** Carries the aws.cognito.signin.user.admin scope: what the browser manages its own user with (section 13.3). */
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  /** Cognito's username claim - what USER_AUTH is started with for a step-up. */
  username?: string;
  /** When the user last proved who they are (id token `auth_time`, epoch seconds); step-up compares against it. */
  authTime?: number;
  /**
   * When this sign-in happened (epoch ms), set once at the callback and kept across step-ups - which renew
   * `auth_time` but are not a sign-in from the user's side. "Do later" on a nudge means "until the next of these".
   */
  signedInAt?: number;
  expiresAt: number;
}

/**
 * The sign-in id ("Do later" on a nudge means "until the next sign-in"). This tab's copy is in
 * sessionStorage, beside the tokens. A new tab has no tokens and goes through managed login, which —
 * while its own session cookie lives — answers without asking and hands back the same `auth_time`;
 * so the id is also kept in localStorage against the `auth_time`s it has been seen with, and a tab
 * whose callback carries one of them joins the same sign-in rather than minting a new one. A step-up
 * (in-app or through a federated provider) adds its new `auth_time` to the same id; it never mints one.
 */
const SIGNED_IN_AT_KEY = "lms.signedInAt";
const SHARED_SIGN_IN_KEY = "lms.signIn";
interface SharedSignIn {
  id: number;
  authTimes: number[];
}
function signedInAtOf(): number | undefined {
  const raw = window.sessionStorage.getItem(SIGNED_IN_AT_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : undefined;
}
function sharedSignIn(): SharedSignIn | undefined {
  try {
    const s = JSON.parse(window.localStorage.getItem(SHARED_SIGN_IN_KEY) ?? "null") as SharedSignIn | null;
    return s && Number.isFinite(s.id) && Array.isArray(s.authTimes) ? s : undefined;
  } catch {
    return undefined;
  }
}
function saveShared(s: SharedSignIn | undefined): void {
  try {
    if (s) window.localStorage.setItem(SHARED_SIGN_IN_KEY, JSON.stringify({ id: s.id, authTimes: s.authTimes.slice(-10) }));
    else window.localStorage.removeItem(SHARED_SIGN_IN_KEY);
  } catch {
    // No storage: each tab is its own sign-in, which only means a nudge put off in one tab shows in the next.
  }
}

/** A callback came back: a step-up keeps this tab's id; anything else joins the sign-in its auth_time belongs to, or starts one. */
export function markSignedIn(authTime: number | undefined, opts: { stepUp?: boolean } = {}): void {
  const current = signedInAtOf();
  if (opts.stepUp && current !== undefined) {
    noteStepUp(authTime);
    return;
  }
  const shared = sharedSignIn();
  const id = authTime !== undefined && shared?.authTimes.includes(authTime) ? shared.id : Date.now();
  window.sessionStorage.setItem(SIGNED_IN_AT_KEY, String(id));
  saveShared({ id, authTimes: id === shared?.id ? shared.authTimes : authTime !== undefined ? [authTime] : [] });
}

/** A session this tab holds with no id (signed in before ids existed): give it one, joining the shared sign-in when its auth_time matches. */
function adoptSignIn(authTime: number | undefined): number | undefined {
  markSignedIn(authTime);
  return signedInAtOf();
}

/** A step-up renewed auth_time without being a sign-in: remember the new auth_time as this sign-in's too. */
function noteStepUp(authTime: number | undefined): void {
  const current = signedInAtOf();
  const shared = sharedSignIn();
  if (current === undefined || authTime === undefined) return;
  saveShared({ id: current, authTimes: shared?.id === current ? [...new Set([...shared.authTimes, authTime])] : [authTime] });
}

/** The tokens a fresh in-app authentication (step-up) hands back. */
export interface FreshTokens {
  IdToken?: string;
  AccessToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

/**
 * ADR-0014: which identity provider to send the browser to. `idpIdentifier`
 * is an email domain registered on the provider (the login page's route);
 * `identityProvider` is the provider's name (a federated step-up, which
 * already knows it from /v1/me). Neither: Cognito's own sign-in.
 */
export interface SignInRoute {
  idpIdentifier?: string;
  identityProvider?: string;
  /** A step-up through the provider, not a sign-in: the callback keeps the sign-in id (nudges' "Do later"). */
  stepUp?: boolean;
}

export interface AuthAdapter {
  getSession(): Promise<Session | null>;
  /** Replaces the stored tokens after a step-up, so the session's auth_time is the new one. */
  replaceTokens(tokens: FreshTokens): Promise<Session | null>;
  signIn(returnTo: string, route?: SignInRoute): Promise<void>;
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
  /** The session changed underneath the app — renewed before expiry, or gone. Returns the unsubscribe. */
  subscribe(listener: (session: Session | null) => void): () => void;
}

/**
 * Cognito managed login via OIDC authorization code + PKCE (the auth
 * template's adapter, verbatim but for the names). Tokens live in
 * sessionStorage (cleared with the tab); the ID token is what the API's
 * JWT authorizer validates. The app client is the one for THIS host
 * (design.md §6): a tenant's own branding, callback and audience.
 */
export function cognitoAuth(config: RuntimeConfig): AuthAdapter {
  const origin = window.location.origin;
  const cognito = config.cognito;
  const clientId = clientIdForHost(config);
  if (!cognito || !clientId) throw new Error("This host serves no tenant");
  const manager = new UserManager({
    authority: cognito.issuer,
    client_id: clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    // The last scope is what lets the access token manage its own user (devices, passkeys, password, authenticator).
    scope: "openid email profile aws.cognito.signin.user.admin",
    // Cognito's ID and access tokens live an hour; the refresh token (in the same store) renews them without a
    // round trip through managed login. Renew a minute before expiry (the library uses the refresh-token grant
    // when it has one, never an iframe) — walk 7 found the owner signed out on the hour with a refresh token
    // sitting unused (gairm/lms#203, finding 10). auth_time survives a refresh, so the step-up clock is unmoved.
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });

  // An expired user with a refresh token is a session to renew, not a signed-out person: a tab reopened after
  // lunch, a laptop woken up. Without a refresh token, or when the refresh fails (revoked, expired after its 30
  // days), the answer is null and the shell offers sign-in.
  const current = async (): Promise<User | null> => {
    const u = await manager.getUser();
    if (!u || !u.expired || !u.refresh_token) return u;
    try {
      return await manager.signinSilent();
    } catch {
      await manager.removeUser();
      return null;
    }
  };

  // `auth_time` is read from the token itself: oidc-client-ts filters it (and other protocol claims) out of `profile`.
  const toSession = (u: User | null): Session | null =>
    u && !u.expired && u.id_token
      ? {
          idToken: u.id_token,
          accessToken: u.access_token,
          refreshToken: u.refresh_token,
          email: u.profile.email,
          username: typeof u.profile["cognito:username"] === "string" ? u.profile["cognito:username"] : undefined,
          authTime: authTimeOf(claimsOf(u.id_token)),
          signedInAt: signedInAtOf() ?? adoptSignIn(authTimeOf(claimsOf(u.id_token))),
          expiresAt: (u.expires_at ?? 0) * 1000,
        }
      : null;

  return {
    getSession: async () => toSession(await current()),
    replaceTokens: async (t) => {
      const before = await manager.getUser();
      if (!t.IdToken || !t.AccessToken) return toSession(before);
      const profile = claimsOf(t.IdToken) as User["profile"];
      const user = new User({
        id_token: t.IdToken,
        access_token: t.AccessToken,
        refresh_token: t.RefreshToken ?? before?.refresh_token,
        token_type: "Bearer",
        scope: before?.scope,
        profile,
        expires_at: Math.floor(Date.now() / 1000) + (t.ExpiresIn ?? 3600),
      });
      await manager.storeUser(user);
      noteStepUp(authTimeOf(claimsOf(t.IdToken)));
      return toSession(user);
    },
    signIn: (returnTo, route) =>
      manager.signinRedirect({
        state: { returnTo, ...(route?.stepUp ? { stepUp: true } : {}) },
        ...(route?.idpIdentifier ? { extraQueryParams: { idp_identifier: route.idpIdentifier } } : route?.identityProvider ? { extraQueryParams: { identity_provider: route.identityProvider } } : {}),
      }),
    completeSignIn: async () => {
      const u = await manager.signinCallback();
      const state = (u?.state ?? {}) as { returnTo?: string; stepUp?: boolean };
      markSignedIn(u?.id_token ? authTimeOf(claimsOf(u.id_token)) : undefined, { stepUp: state.stepUp === true });
      return state.returnTo ?? "/";
    },
    signOut: async () => {
      await manager.removeUser();
      // The next sign-in is a new one in every tab.
      saveShared(undefined);
      window.sessionStorage.removeItem(SIGNED_IN_AT_KEY);
      // Cognito's discovery document has no end_session_endpoint; its logout URL is the Hosted UI's.
      const url = new URL(`https://${cognito.domain}/logout`);
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("logout_uri", `${origin}/`);
      window.location.assign(url.toString());
    },
    subscribe: (listener) => {
      // The automatic renew stores a new user; the app's copy of the tokens must follow, or the next API call
      // still carries the old, expiring ID token. A renew that fails leaves the current session to run out.
      const loaded = (u: User) => listener(toSession(u));
      const unloaded = () => listener(null);
      manager.events.addUserLoaded(loaded);
      manager.events.addUserUnloaded(unloaded);
      return () => {
        manager.events.removeUserLoaded(loaded);
        manager.events.removeUserUnloaded(unloaded);
      };
    },
  };
}

/** Epoch seconds from the `auth_time` claim, whether the issuer wrote it as a number or a string. */
export function authTimeOf(claims: Record<string, unknown>): number | undefined {
  const raw = claims["auth_time"];
  const n = typeof raw === "number" ? raw : typeof raw === "string" && /^\d+$/.test(raw) ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/** The id token's claims, read without verification: the API verifies; the browser only reads what it was given. */
export function claimsOf(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split(".")[1] ?? "";
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** `mockAuth`: a fake session whose persona is chosen on the login screen (local dev and the e2e). */
export function mockAuth(): AuthAdapter {
  const KEY = "mock-session";
  return {
    getSession: async () => {
      const raw = window.sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    },
    replaceTokens: async (t) => {
      const raw = window.sessionStorage.getItem(KEY);
      if (!raw) return null;
      const s = JSON.parse(raw) as Session;
      const next: Session = { ...s, authTime: Math.floor(Date.now() / 1000), ...(t.AccessToken ? { accessToken: t.AccessToken } : {}) };
      window.sessionStorage.setItem(KEY, JSON.stringify(next));
      return next;
    },
    signIn: async (returnTo, route) => {
      // No Hosted UI to go to: remember where the real adapter would have sent the browser, for the e2e.
      window.sessionStorage.setItem("mock-sign-in-route", JSON.stringify({ returnTo, ...route }));
      window.location.assign("/login");
    },
    completeSignIn: async () => "/",
    signOut: async () => {
      window.sessionStorage.removeItem(KEY);
      // Where the real adapter lands: Cognito's logout returns to the app client's logout URL, the tenant's Home.
      window.location.assign("/");
    },
    subscribe: () => () => undefined,
  };
}

/** A mock session carrying a MeResponse as its "token"; the mock API reads it back (fixtures.ts). */
export function setMockSession(me: MeResponse): void {
  const idToken = `mock.${btoa(JSON.stringify(me))}`;
  window.sessionStorage.setItem(
    "mock-session",
    JSON.stringify({ idToken, accessToken: idToken, email: me.person.email, username: me.person.email, authTime: Math.floor(Date.now() / 1000), signedInAt: Date.now(), expiresAt: Date.now() + 8 * 3_600_000 } satisfies Session),
  );
}
